import { useEffect, useRef } from "react";
import { useWebSocket } from "@/context/WebSocketProvider";

const DEFAULT_MIN_INTERVAL_MS = 30_000;

/**
 * Runs a callback after WebSocket reconnects, at most once per `minIntervalMs`.
 *
 * While the socket is down, events are missed, so any query or state container
 * that relies on socket events must reconcile its state when the connection is
 * restored. This hook fires `callback` on a genuine reconnect (a
 * `disconnected → connected` transition) and never on the first connect.
 *
 * The first reconnect fires immediately. Reconnects inside the window collapse
 * into one trailing call at the end of it, so a flapping socket can't turn into
 * a refetch loop, yet state is still reconciled after the last reconnect.
 *
 * @param callback - invoked after a reconnect. The latest reference is always used.
 * @param minIntervalMs - minimum time between calls.
 *
 * @example
 * // Refetch after the socket recovers from an outage.
 * useReconnectRefetch(() => {
 *   queryClient.invalidateQueries({ queryKey: ["integrations"] });
 * });
 */
export const useReconnectRefetch = (
  callback: () => void,
  minIntervalMs: number = DEFAULT_MIN_INTERVAL_MS,
): void => {
  const { reconnectNonce } = useWebSocket();

  // Keep the latest callback without re-running the effect when it changes.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Track the last nonce we acted on so we skip the initial mount and only
  // fire when the nonce actually increases.
  const lastNonceRef = useRef(reconnectNonce);
  const lastFiredAtRef = useRef(-Infinity);
  const trailingTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (reconnectNonce === lastNonceRef.current) return;
    lastNonceRef.current = reconnectNonce;

    const fire = () => {
      trailingTimerRef.current = undefined;
      lastFiredAtRef.current = Date.now();
      callbackRef.current();
    };

    const wait = lastFiredAtRef.current + minIntervalMs - Date.now();
    if (wait <= 0) fire();
    else if (!trailingTimerRef.current) {
      trailingTimerRef.current = setTimeout(fire, wait);
    }
  }, [reconnectNonce, minIntervalMs]);

  useEffect(() => () => clearTimeout(trailingTimerRef.current), []);
};
